"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var kentico_cloud_core_1 = require("kentico-cloud-core");
var fields_1 = require("../fields");
var models_1 = require("../models");
var resolvers_1 = require("../resolvers");
var FieldMapper = /** @class */ (function () {
    function FieldMapper(config, richTextHtmlParser) {
        this.config = config;
        this.richTextHtmlParser = richTextHtmlParser;
        this.defaultLinkedItemWrapperTag = 'p';
        this.defaultLinkedItemWrapperClasses = ['kc-linked-item-wrapper'];
    }
    /**
     * Maps all fields in given content item and returns strongly typed content item based on the resolver specified
     * in DeliveryClientConfig
     * @param item Item to map (raw response from Kentico Cloud)
     * @param modularContent Modular content sent along with item
     * @param queryConfig Query configuration
     */
    FieldMapper.prototype.mapFields = function (item, modularContent, queryConfig, processedItems) {
        var _this = this;
        if (!item) {
            throw Error("Cannot map fields because item is not defined");
        }
        if (!item.system) {
            throw Error("Cannot map item because it does not contain system attributes. This is an essential field and every item should have one.");
        }
        if (!item.elements) {
            throw Error("Cannot map elements of item with codename '" + item.system.codename + "'");
        }
        if (!processedItems) {
            throw Error("ProcessedItems need to be initialized");
        }
        if (!Array.isArray(processedItems)) {
            throw Error("ProcessedItems need to be an array");
        }
        var properties = Object.getOwnPropertyNames(item.elements);
        var itemTyped;
        // check if resolver for this type is available
        if (this.config.typeResolvers && resolvers_1.stronglyTypedResolver.hasTypeResolver(item.system.type, this.config.typeResolvers)) {
            itemTyped = resolvers_1.stronglyTypedResolver.createTypedObj(item.system.type, item, this.config.typeResolvers);
        }
        else {
            itemTyped = resolvers_1.stronglyTypedResolver.createContentItem(item);
        }
        // add/taken item to processed items to avoid infinite recursion
        var processedItem = processedItems.find(function (m) { return m.system.codename === item.system.codename; });
        if (!processedItem) {
            processedItems.push(itemTyped);
        }
        properties.forEach(function (fieldName) {
            var field = item.elements[fieldName];
            var propertyName;
            // resolve field to a custom model property
            if (itemTyped.propertyResolver) {
                propertyName = itemTyped.propertyResolver(fieldName);
            }
            // if property hasn't been resolved, try with decorator
            if (!propertyName) {
                propertyName = fields_1.FieldDecorators.getPropertyName(itemTyped, fieldName);
            }
            // if property name is null/empty, use elements codename
            if (!propertyName) {
                propertyName = fieldName;
            }
            itemTyped[propertyName] = _this.mapField(field, modularContent, itemTyped, queryConfig, processedItems);
        });
        return itemTyped;
    };
    FieldMapper.prototype.mapField = function (field, modularContent, item, queryConfig, processedItems) {
        var fieldType = kentico_cloud_core_1.enumHelper.getEnumFromValue(fields_1.FieldType, field.type);
        if (fieldType) {
            if (fieldType === fields_1.FieldType.ModularContent) {
                return this.mapLinkedItemsField(field, modularContent, queryConfig, processedItems);
            }
            if (fieldType === fields_1.FieldType.Text) {
                return this.mapTextField(field);
            }
            if (fieldType === fields_1.FieldType.Asset) {
                return this.mapAssetsField(field);
            }
            if (fieldType === fields_1.FieldType.Number) {
                return this.mapNumberField(field);
            }
            if (fieldType === fields_1.FieldType.MultipleChoice) {
                return this.mapMultipleChoiceField(field);
            }
            if (fieldType === fields_1.FieldType.DateTime) {
                return this.mapDateTimeField(field);
            }
            if (fieldType === fields_1.FieldType.RichText) {
                return this.mapRichTextField(field, modularContent, queryConfig, processedItems);
            }
            if (fieldType === fields_1.FieldType.UrlSlug) {
                return this.mapUrlSlugField(field, item, queryConfig);
            }
            if (fieldType === fields_1.FieldType.Taxonomy) {
                return this.mapTaxonomyField(field);
            }
            if (fieldType === fields_1.FieldType.Custom) {
                return this.mapCustomField(field, item.system.type);
            }
        }
        console.warn("Skipping unknown field type '" + field.type + "' of field '" + field.name + "'");
        return undefined;
    };
    FieldMapper.prototype.mapRichTextField = function (field, modularContent, queryConfig, processedItems) {
        var _this = this;
        // get all linked items nested in rich text
        var linkedItems = [];
        if (field.modular_content) {
            if (Array.isArray(field.modular_content)) {
                field.modular_content.forEach(function (codename) {
                    // get linked item and check if it exists (it might not be included in response due to 'Depth' parameter)
                    var rawItem = modularContent[codename];
                    // first try to get existing item
                    var existingLinkedItem = _this.getOrSaveLinkedItem(codename, field, queryConfig, modularContent, processedItems);
                    if (existingLinkedItem) {
                        // item was found, add it to linked items
                        linkedItems.push(existingLinkedItem);
                    }
                    else {
                        var throwErrorForMissingLinkedItems = false;
                        // check if errors should be thrown for missing linked items
                        if (queryConfig.throwErrorForMissingLinkedItems === false || queryConfig.throwErrorForMissingLinkedItems === true) {
                            // variable is a boolean
                            throwErrorForMissingLinkedItems = queryConfig.throwErrorForMissingLinkedItems;
                        }
                        // throw error if raw item is not available and errors are not skipped
                        if (!rawItem) {
                            var msg = "Mapping RichTextField field '" + field.name + "' failed because referenced linked item with codename '" + codename + "' could not be found in Delivery response.\n                            Increasing 'depth' parameter may solve this issue as it will include nested items. Alternatively you may disable 'throwErrorForMissingLinkedItems' in your query";
                            if (throwErrorForMissingLinkedItems) {
                                throw Error(msg);
                            }
                        }
                        // item was not found or not yet resolved
                        if (rawItem) {
                            var mappedLinkedItem = _this.mapFields(rawItem, modularContent, queryConfig, processedItems);
                            // add mapped linked item to result
                            if (mappedLinkedItem) {
                                linkedItems.push(mappedLinkedItem);
                            }
                        }
                    }
                });
            }
        }
        // extract and map links
        var links = this.mapRichTextLinks(field.links);
        return new fields_1.Fields.RichTextField(field.name, field.value, field.modular_content, {
            links: links,
            resolveHtml: function () { return resolvers_1.richTextResolver.resolveHtml(field.value, {
                enableAdvancedLogging: _this.config.enableAdvancedLogging ? _this.config.enableAdvancedLogging : false,
                typeResolvers: _this.config.typeResolvers ? _this.config.typeResolvers : [],
                richTextHtmlParser: _this.richTextHtmlParser,
                getLinkedItem: function (codename) { return _this.getOrSaveLinkedItem(codename, field, queryConfig, modularContent, linkedItems); },
                links: links,
                queryConfig: queryConfig,
                linkedItemWrapperTag: _this.config.linkedItemResolver && _this.config.linkedItemResolver.linkedItemWrapperTag
                    ? _this.config.linkedItemResolver.linkedItemWrapperTag
                    : _this.defaultLinkedItemWrapperTag,
                linkedItemWrapperClasses: _this.config.linkedItemResolver && _this.config.linkedItemResolver.linkedItemWrapperClasses
                    ? _this.config.linkedItemResolver.linkedItemWrapperClasses
                    : _this.defaultLinkedItemWrapperClasses
            }); }
        });
    };
    FieldMapper.prototype.mapDateTimeField = function (field) {
        return new fields_1.Fields.DateTimeField(field.name, field.value);
    };
    FieldMapper.prototype.mapMultipleChoiceField = function (field) {
        return new fields_1.Fields.MultipleChoiceField(field.name, field.value);
    };
    FieldMapper.prototype.mapNumberField = function (field) {
        return new fields_1.Fields.NumberField(field.name, field.value);
    };
    FieldMapper.prototype.mapTextField = function (field) {
        return new fields_1.Fields.TextField(field.name, field.value);
    };
    FieldMapper.prototype.mapAssetsField = function (field) {
        return new fields_1.Fields.AssetsField(field.name, field.value);
    };
    FieldMapper.prototype.mapTaxonomyField = function (field) {
        return new fields_1.Fields.TaxonomyField(field.name, field.value, field.taxonomy_group);
    };
    FieldMapper.prototype.mapCustomField = function (field, contentType) {
        // try to find field resolver
        if (this.config.fieldResolver) {
            var customModel = this.config.fieldResolver(contentType, field.name, field.value);
            if (customModel) {
                return customModel;
            }
        }
        return new fields_1.Fields.CustomField(field.name, field.value);
    };
    FieldMapper.prototype.mapUrlSlugField = function (field, item, queryConfig) {
        var _this = this;
        var linkResolver = this.getLinkResolverForUrlSlugField(item, queryConfig);
        return new fields_1.Fields.UrlSlugField(field.name, field.value, {
            resolveLink: function () { return resolvers_1.urlSlugResolver.resolveUrl({
                fieldName: field.name,
                type: field.type,
                fieldValue: field.value,
                item: item,
                enableAdvancedLogging: _this.config.enableAdvancedLogging ? _this.config.enableAdvancedLogging : false,
                linkResolver: linkResolver
            }); }
        });
    };
    FieldMapper.prototype.mapLinkedItemsField = function (field, modularContent, queryConfig, processedItems) {
        var _this = this;
        if (!field) {
            if (this.config.enableAdvancedLogging) {
                console.warn("Cannot map linked item field because field does not exist. This warning can be turned off by disabling 'enableAdvancedLogging' option.");
            }
            return [];
        }
        if (!field.value) {
            if (this.config.enableAdvancedLogging) {
                console.warn("Cannot map linked item of '" + field.name + "' because its value does not exist. This warning can be turned off by disabling 'enableAdvancedLogging' option.");
            }
            return [];
        }
        // result is always an array of content items
        var result = [];
        // value = array of item codenames
        var linkedItemCodenames = field.value;
        linkedItemCodenames.forEach(function (codename) {
            var linkedItem = _this.getOrSaveLinkedItem(codename, field, queryConfig, modularContent, processedItems);
            if (linkedItem) {
                // add item to result
                result.push(linkedItem);
            }
            else {
                // item was not found
                if (_this.config.enableAdvancedLogging) {
                    // tslint:disable-next-line:max-line-length
                    console.warn("Linked item with codename '" + codename + "' in linked items field '" + field.name + "' of '" + field.type + "' type could not be found. If you require this item, consider increasing 'depth' of your query. This warning can be turned off by disabling 'enableAdvancedLogging' option.");
                }
            }
        });
        return result;
    };
    FieldMapper.prototype.getLinkResolverForUrlSlugField = function (item, queryConfig) {
        // link resolver defined by the query config (= by calling method) has priority over type's global link resolver
        var linkResolver = undefined;
        if (queryConfig.linkResolver) {
            linkResolver = queryConfig.linkResolver;
        }
        else {
            linkResolver = item.linkResolver;
        }
        return linkResolver;
    };
    FieldMapper.prototype.getOrSaveLinkedItem = function (codename, field, queryConfig, modularContent, processedItems) {
        // first check if item was already resolved and return it if it was
        var existingItem = processedItems.find(function (m) { return m.system.codename === codename; });
        if (existingItem) {
            return existingItem;
        }
        var rawItem = modularContent[codename];
        // by default errors are not thrown
        var throwErrorForMissingLinkedItems = false;
        // check if errors should be thrown for missing linked items
        if (queryConfig.throwErrorForMissingLinkedItems === false || queryConfig.throwErrorForMissingLinkedItems === true) {
            // variable is a boolean
            throwErrorForMissingLinkedItems = queryConfig.throwErrorForMissingLinkedItems;
        }
        // throw error if item is not in response and errors are not skipped
        if (!rawItem) {
            var msg = "Linked item with codename '" + codename + "' could not be found in Delivery response.\n            This linked item was requested by '" + field.name + "' field of '" + field.type + "'.\n            Error can usually be solved by increasing 'Depth' parameter of your query.\n            Alternatively, you may prevent this error by disabling 'throwErrorForMissingLinkedItems' in query configuration.";
            if (throwErrorForMissingLinkedItems) {
                throw Error(msg);
            }
        }
        if (!rawItem) {
            // nothing else to do when item is not in response
            return undefined;
        }
        var mappedLinkedItem;
        // try resolving item using custom item resolver if its set
        if (queryConfig.itemResolver) {
            var customMappedItem = queryConfig.itemResolver(field, rawItem, modularContent, queryConfig);
            if (customMappedItem) {
                // if user used custom mapping, make sure to add 'system' and 'elements' properties to result
                customMappedItem.system = resolvers_1.stronglyTypedResolver.mapSystemAttributes(rawItem.system);
                customMappedItem.elements = rawItem.elements;
                mappedLinkedItem = customMappedItem;
            }
        }
        // original resolving if item is still undefined
        if (!mappedLinkedItem) {
            mappedLinkedItem = this.mapFields(rawItem, modularContent, queryConfig, processedItems);
        }
        // add to processed items
        processedItems.push(mappedLinkedItem);
        return mappedLinkedItem;
    };
    FieldMapper.prototype.mapRichTextLinks = function (linksJson) {
        var links = [];
        for (var _i = 0, _a = Object.keys(linksJson); _i < _a.length; _i++) {
            var linkItemId = _a[_i];
            var linkRaw = linksJson[linkItemId];
            links.push(new models_1.Link({
                codename: linkRaw.codename,
                itemId: linkItemId,
                urlSlug: linkRaw.url_slug,
                type: linkRaw.type,
            }));
        }
        return links;
    };
    return FieldMapper;
}());
exports.FieldMapper = FieldMapper;
//# sourceMappingURL=field.mapper.js.map